import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SseService } from '@ortho-next/sse';
import { finalize, map, switchMap } from 'rxjs';
import { BoneViewEnum, DeviceEnum, LoaderService } from '../../core';
import { AttachmentPreviewComponent, AttachmentUploaderComponent, ImageGarageComponent } from '../../gallery';
import { Attachment, IPlanControl, PlanControl, planErrorMsgs } from '../../models';
import { AttachmentService, PlanFormService } from '../../services';
import { BaseComponent, ValidationMessagePipe } from '../../shared';


/**
 * Plan image section component.
 */
@Component({
	selector: 'plan-image',
	templateUrl: './plan-image.component.html',
	imports: [
		AsyncPipe,
		ValidationMessagePipe,
		AttachmentUploaderComponent
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class PlanImageComponent extends BaseComponent implements OnInit, OnDestroy {

	private readonly sse = inject(SseService);
	private readonly modalSrv = inject(NgbModal);
	private readonly loaderSrv = inject(LoaderService);
	private readonly attachSrv = inject(AttachmentService);
	private readonly planFormSrv = inject(PlanFormService);


	title = this.labels().IMAGE_LOADER_COMPONENT_TITLE_TLHEX;
	form: FormGroup<IPlanControl> = new FormGroup<IPlanControl>(new PlanControl());

	apImage = toSignal(this.attachSrv.getApImage());
	isApLoading = toSignal(this.attachSrv.isApLoading());
	ltImage = toSignal(this.attachSrv.getLtImage());
	isLtLoading = toSignal(this.attachSrv.isLtLoading());
	isReadonly = false;

	readonly boneViewEnum = BoneViewEnum;
	readonly errorMsgs = planErrorMsgs;


	get isBoxDisabled(): boolean { return this.planFormSrv.isImagesBoxDisabled; }
	get isImagesValid(): boolean { return this.form.controls.apImage.valid && this.form.controls.ltImage.valid; }

	ngOnInit(): void {
		this.planFormSrv.getForm().subscribe(form => {
			this.form = form;
			this.title = this._title(form.value.device);
			this.isReadonly = this.planFormSrv.isReadonly;
		});
		this.sse.subscribe(`OrthoNext.Image.Thumb`, (res: { image: string, error: boolean }) => {
			console.log(`Thumbnail: ${res.image}, error: ${res.error}`);
			this.attachSrv.resetLoading(res.image);
		});
	}

	private _title(device?: DeviceEnum | null): string {
		switch (device) {
			case DeviceEnum.Fitbone: return this.labels().IMAGE_LOADER_COMPONENT_TITLE_FITBONE;
			case DeviceEnum.Jps: return this.labels().IMAGE_LOADER_COMPONENT_TITLE_JPS;
			default: return this.labels().IMAGE_LOADER_COMPONENT_TITLE_TLHEX;
		}
	}

	/**
	* Reset AP image.
	*/
	removeApImage(): void {
		this.attachSrv.setApImageId(null, false);
	}

	/**
	* Reset LT image.
	*/
	removeLtImage(): void {
		this.attachSrv.setLtImageId(null, false);
	}

	/**
	* Open image preview modal.
	*/
	openPreview(image: Attachment): void {
		if (!this.modalSrv.hasOpenModals()) {
			this.attachSrv.getImageDetail(image.id, this.planFormSrv.userId).subscribe(img => {
				const modalRef = this.modalSrv.open(AttachmentPreviewComponent, {
					centered: true, backdrop: 'static', windowClass: 'modal-image'
				});
				(modalRef.componentInstance as AttachmentPreviewComponent).image = img;
				(modalRef.componentInstance as AttachmentPreviewComponent).fileName.set(image.fileName);
			});
		}
	}

	/**
	* Open image categorization modal to edit.
	*/
	openCategorizationModal(imageId: string, fileName: string, view: BoneViewEnum): void {
		if (!this.modalSrv.hasOpenModals()) {
			this.loaderSrv.show();
			this.attachSrv.getImageDetail(imageId, this.planFormSrv.userId).pipe(
				switchMap(img => this.attachSrv.getFileByURL(img.url, fileName).pipe(map(file => ({ img, file })))),
				finalize(() => this.loaderSrv.hide())
			).subscribe(({ img, file }) => {
				const modalRef = this.modalSrv.open(ImageGarageComponent, {
					centered: true, backdrop: 'static', windowClass: 'modal-image-garage font-size-bigger', size: 'xl'
				});
				(modalRef.componentInstance as ImageGarageComponent).edit.set(true);
				(modalRef.componentInstance as ImageGarageComponent).view.set(view);
				(modalRef.componentInstance as ImageGarageComponent).image.set(this.attachSrv.initEditImage(img, file));
			});
		}
	}


	ngOnDestroy(): void {
		this.sse.unsubscribe(`OrthoNext.Image.Thumb`);
	}

}
